import { create } from 'zustand';

/**
 * Lightweight per-tab error flag store for TC v4.
 * Mirrors the tabErrors / setTabError / clearTabErrors pattern from scStandard.store.ts.
 *
 * Usage in a tab component:
 *   const { setTabError } = useTcTabErrorStore();
 *   useEffect(() => {
 *     setTabError('product-details', violations.length > 0);
 *   }, [violations.length]);
 *
 * Usage in the parent stepper (request-tc/index.tsx):
 *   const { tabErrors } = useTcTabErrorStore();
 *   // then pass `warning: Boolean(tabErrors[tab.id])` to each tab object.
 *
 * rmdfViolatingArticles — the article numbers of products that are missing
 * Feedstock / ReclaimedSource because they are linked to an RMDF incoming TC.
 * Written by IncomingTcDetails, read by CertifiedProductsComponents to render
 * field-level error pills without a second API call.
 */

interface TcTabErrorState {
  /** Map of tab-id → hasError flag */
  tabErrors: Record<string, boolean>;
  /** Set or clear the error flag for a single tab */
  setTabError: (tabId: string, hasError: boolean) => void;
  /** Clear all flags (call when the user leaves the TC or TE_ID changes) */
  clearTabErrors: () => void;

  /**
   * Article numbers that are currently violating the RMDF requirement.
   * Structure: { [articleNo]: { missingFeedstock: boolean, missingReclaimedSource: boolean } }
   */
  rmdfViolations: Record<string, { missingFeedstock: boolean; missingReclaimedSource: boolean }>;
  setRmdfViolations: (violations: Record<string, { missingFeedstock: boolean; missingReclaimedSource: boolean }>) => void;
  clearRmdfViolations: () => void;
}

export const useTcTabErrorStore = create<TcTabErrorState>()((set) => ({
  tabErrors: {},
  setTabError: (tabId, hasError) =>
    set((state) => ({
      tabErrors: { ...state.tabErrors, [tabId]: hasError },
    })),
  clearTabErrors: () => set({ tabErrors: {} }),

  rmdfViolations: {},
  setRmdfViolations: (violations) => set({ rmdfViolations: violations }),
  clearRmdfViolations: () => set({ rmdfViolations: {} }),
}));
